import React, { useMemo } from 'react';
import { Box, Typography, Grid } from '@mui/material';

import CollapseCard from "components/Collapse";
import FirmwareComplianceChart from 'components/FirmwareComplianceChart';

import icn_pie_chart from 'assets/images/icons/icn_pie_chart.png';

const FirmwareUpdateDrawer = ({ chartData }) => {

  const { latestCount = 0, previousCount = 0, legacyCount = 0 } = chartData || {};
  const total = latestCount + previousCount + legacyCount;

  const percentages = useMemo(() => {
    if (total === 0) return { latest: 0, previous: 0, legacy: 0 };
    return {
      latest: Math.round((latestCount / total) * 100),
      previous: Math.round((previousCount / total) * 100),
      legacy: Math.round((legacyCount / total) * 100)
    };
  }, [total, latestCount, previousCount, legacyCount]);

  const displayData = useMemo(() => [
    { value: latestCount, name: 'Latest Version', itemStyle: { color: '#172664' } },
    { value: previousCount, name: 'Previous Version', itemStyle: { color: '#87A2E2' } },
    { value: legacyCount, name: 'Legacy', itemStyle: { color: '#008000' } }
  ], [latestCount, previousCount, legacyCount]);

  const legendFormatter = (name) => {
    const val = name === 'Latest Version' ? percentages.latest : (name === 'Previous Version' ? percentages.previous : percentages.legacy);
    return `{name|${name}} {percent|${val}%}`;
  };

  const analysis = useMemo(() => {
    const { latest, legacy } = percentages;

    /**
     * Algorithm for Firmware Analysis:
     *
     * | Confidence | Distribution Metric (Example) | Impact Level | Action               |
     * |------------|-------------------------------|--------------|----------------------|
     * | Good       | Latest > 80                   | Low          | Monitor only.        |
     * | WARN       | Legacy > 10 or Latest < 80    | Moderate     | Schedule updates.    |
     * | BAD        | Legacy > 30                   | High         | Urgent intervention. |
     */
    if (legacy > 30) {
      return {
        confidence: 'BAD',
        impact: 'High',
        impactColor: '#E53E3E' // High impact color (Red)
      };
    } else if (legacy > 10 || latest < 80) {
      return {
        confidence: 'WARN',
        impact: 'Moderate',
        impactColor: '#00A5CF' // Moderate impact color (Light Blue)
      };
    } else {
      return {
        confidence: 'Good',
        impact: 'Low',
        impactColor: '#38A169' // Low impact color (Green)
      };
    }
  }, [percentages]);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Title */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={icn_pie_chart} sx={{ width: '20px', height: '20px', mr: 1.5 }} />
        <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '18px' }}>
          Firmware Update Status
        </Typography>
      </Box>

      {/* Firmware Status Chart */}
      <CollapseCard title="Firmware Status">
        <Box sx={{ width: "100%", pt: 2 }}>
          <FirmwareComplianceChart
            data={displayData}
            totalValue={total}
            legendFormatter={legendFormatter}
            height="200px"
          />
        </Box>
      </CollapseCard>

      {/* Analysis Section */}
      <CollapseCard title="Analysis">
        <Box sx={{ py: 1 }}>
          <Grid container spacing={3}>
            {/* Confidence & Impact Row */}
            <Grid item xs={6} sx={{ display: 'none' }}>
              <Typography sx={{ color: '#718096', fontSize: '13px', mb: 0.5 }}>Confidence</Typography>
              <Typography sx={{ color: '#172664', fontSize: '28px', fontWeight: 'bold' }}>{analysis.confidence}</Typography>
            </Grid>
            <Grid item xs={6} sx={{ display: 'none' }}>
              <Typography sx={{ color: '#718096', fontSize: '13px', mb: 0.5 }}>Impact</Typography>
              <Typography sx={{ color: analysis.impactColor, fontSize: '28px', fontWeight: 'bold' }}>{analysis.impact}</Typography>
            </Grid>

            {/* Percentages Rows */}
            <Grid item xs={6}>
              <Typography sx={{ color: '#718096', fontSize: '13px', mb: 0.5 }}>Latest</Typography>
              <Typography sx={{ color: '#172664', fontSize: '28px', fontWeight: 'bold' }}>{percentages.latest}%</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ color: '#718096', fontSize: '13px', mb: 0.5 }}>Previous</Typography>
              <Typography sx={{ color: '#172664', fontSize: '28px', fontWeight: 'bold' }}>{percentages.previous}%</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ color: '#718096', fontSize: '13px', mb: 0.5 }}>Legacy</Typography>
              <Typography sx={{ color: '#172664', fontSize: '28px', fontWeight: 'bold' }}>{percentages.legacy}%</Typography>
            </Grid>
          </Grid>
        </Box>
      </CollapseCard>

      {/* Recommendation Section */}
      <CollapseCard title="Recommendation">
        <Box sx={{ py: 1 }}>
          <Typography sx={{ color: '#4A5568', fontSize: '13px', lineHeight: 1.6 }}>
            Based on the detected pattern for Firmware Status, we recommend reviewing the affected devices. If severity is high, verify physical layer or configuration immediately.
          </Typography>
        </Box>
      </CollapseCard>
    </Box>
  );
};

export default FirmwareUpdateDrawer;
